import { LOAD_RESERVATION_OFFER_OPTION } from "@/actions/LOAD_RESERVATION_OFFER.ts";
import { LOAD_RESERVATION_OFFER_OPTION_RATE_PLAN } from "@/actions/LOAD_RESERVATION_OFFER_RATE_PLAN.ts";
import { useLazyGetAvailableRateGroups } from "@/actions/hooks/useLazyGetAvailableRateGroups.ts";
import { Language } from "@/components/Inputs/LanguagePicker/Language.ts";
import { WarningToast } from "@/components/Notifications/WarningToast.tsx";
import { URLParam } from "@/helpers/interfaces/URLParam.ts";
import { transformCarrierPayloadToFormData } from "@/helpers/transformCarrierPayloadToFormData.tsx";
import {
  LoadReservationOfferOptionInput,
  LoadReservationOfferOptionPayload,
  ReservationOfferAccommodation,
  ReservationOfferCarrier,
} from "@/helpers/types/types.ts";
import { useCalculateCart } from "@/helpers/useCalculateCart.tsx";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useMutation } from "@apollo/client";
import i18n, { t } from "i18next";
import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useShallow } from "zustand/react/shallow";

const supportedLanguages: ReadonlySet<string> = new Set(Object.values(Language));

export interface LROPayload {
  loadReservationOfferOption: LoadReservationOfferOptionPayload;
}

export interface LROInput {
  loadReservationOfferOptionInput: LoadReservationOfferOptionInput;
}

export interface LRORatePlanPayload {
  loadReservationOfferOption: LoadReservationOfferOptionPayload;
}

export interface LRORatePlanInput {
  loadReservationOfferOptionInput: LoadReservationOfferOptionInput;
  from: Date;
  until: Date;
}

export const useLoadReservationOffer = () => {
  const [error, setError] = useState<Error | null>(null);
  const toastIdRef = useRef<string | null>(null);

  const {
    resetCartItems,
    setFullCartAccommodationItems,
    setPage,
    setReservationHolderData,
    setReservationOfferLoading,
  } = useEngineStore(
    useShallow((state) => ({
      resetCartItems: state.resetCartItems,
      setFullCartAccommodationItems: state.setFullCartAccommodationItems,
      setPage: state.setPage,
      setReservationHolderData: state.setReservationHolderData,
      setReservationOfferLoading: state.setReservationOfferLoading,
    })),
  );

  const { handleCalculateCart } = useCalculateCart();
  const { handleFetchAvailableRGs } = useLazyGetAvailableRateGroups();

  const [loadReservationOfferOptionMutation] = useMutation<LROPayload, LROInput>(
    LOAD_RESERVATION_OFFER_OPTION,
    {
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    },
  );

  const [loadReservationOfferOptionRatePlanMutation] = useMutation<LRORatePlanPayload, LRORatePlanInput>(
    LOAD_RESERVATION_OFFER_OPTION_RATE_PLAN,
    {
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    },
  );

  const mergeAccommodationData = useCallback(
    (
      baseAccommodations: ReservationOfferAccommodation[],
      ratePlanAccommodations: LRORatePlanPayload["loadReservationOfferOption"]["reservationOfferOption"]["accommodations"],
    ): ReservationOfferAccommodation[] => {
      return baseAccommodations.map((baseAccom) => {
        const ratePlanAccom = ratePlanAccommodations.find((rpAccom) => rpAccom.id === baseAccom.id);

        if (!ratePlanAccom) {
          console.warn(`No rate plan data found for accommodation ${baseAccom.id}`);
          return baseAccom;
        }

        return {
          ...baseAccom,
          ratePlan: {
            ...baseAccom.ratePlan,
            ...ratePlanAccom.ratePlan,
          },
        };
      });
    },
    [],
  );

  const handleOnCompleted = useCallback(
    (mergedAccommodations: ReservationOfferAccommodation[], carrier?: ReservationOfferCarrier) => {
      if (mergedAccommodations && mergedAccommodations.length > 0) {
        setFullCartAccommodationItems(mergedAccommodations);

        if (carrier) {
          const carrierFormData = transformCarrierPayloadToFormData(carrier);
          setReservationHolderData(carrierFormData);
        }
      } else {
        resetCartItems();
      }
    },
    [resetCartItems, setFullCartAccommodationItems, setReservationHolderData],
  );

  const handleRemoveReservationOfferURLParam = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(URLParam.ReservationOffer);
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}?${urlParams.toString()}`,
    );
  }, []);

  const handleLoadReservationOfferOptionWithRatePlan = useCallback(
    async (reservationOfferOptionId: string) => {
      try {
        setReservationOfferLoading(true);
        setError(null);

        // Load basic reservation offer option data first to get dates
        const baseResponse = await loadReservationOfferOptionMutation({
          variables: { loadReservationOfferOptionInput: { reservationOfferOptionId } },
        });

        const reservationOfferOption = baseResponse.data?.loadReservationOfferOption.reservationOfferOption;
        const carrier = baseResponse.data?.loadReservationOfferOption.carrier ?? undefined;

        const offerLanguage = baseResponse.data?.loadReservationOfferOption.language;
        if (!baseResponse.errors?.length && offerLanguage) {
          const resolvedLanguage = supportedLanguages.has(offerLanguage) ? offerLanguage : Language.EN;
          void i18n.changeLanguage(resolvedLanguage);
          localStorage.setItem("i18nextLng", resolvedLanguage);
        }

        // Check if the mutation actually succeeded and has data
        if (!reservationOfferOption?.accommodations?.length) {
          console.warn("No accommodations found in reservation offer option");
          resetCartItems();
          return;
        }

        // Get date range from first accommodation for detailed rate plan data
        const firstAccommodation = reservationOfferOption.accommodations[0];
        const from = new Date(firstAccommodation.checkIn);
        const until = new Date(firstAccommodation.checkOut);

        // Fetch available rate groups needed for cart conversion (pass offer dates explicitly
        // since the store's defaultDateRange hasn't been updated yet at this point)
        await handleFetchAvailableRGs([from, until]);

        // Load detailed rate plan data with the dates
        const ratePlanResponse = await loadReservationOfferOptionRatePlanMutation({
          variables: {
            loadReservationOfferOptionInput: { reservationOfferOptionId },
            from,
            until,
          },
        });

        const ratePlanAccommodations =
          ratePlanResponse.data?.loadReservationOfferOption.reservationOfferOption.accommodations;

        if (!ratePlanAccommodations) {
          console.warn("Failed to load rate plan data");
          handleOnCompleted(reservationOfferOption.accommodations, carrier);
          return;
        }

        const mergedAccommodations = mergeAccommodationData(
          reservationOfferOption.accommodations,
          ratePlanAccommodations,
        );

        handleOnCompleted(mergedAccommodations, carrier);
        setPage(AppPage.ExtraOptions, "replace");

        // Wait for store to update, then calculate cart with the loaded reservation data
        void handleCalculateCart();

        // Wait for extra options & calculate loadings to complete
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (error) {
        console.error("Error loading reservation offer option:", error);

        toastIdRef.current = toast.custom(<WarningToast message={t("offerNotValidAnymore")} />, {
          id: toastIdRef?.current || undefined,
        });
        setError(error as Error);
        resetCartItems();
        handleRemoveReservationOfferURLParam();

        setPage(AppPage.AccommodationPicking, "replace");
      } finally {
        setReservationOfferLoading(false);
      }
    },
    [
      loadReservationOfferOptionMutation,
      loadReservationOfferOptionRatePlanMutation,
      mergeAccommodationData,
      handleOnCompleted,
      resetCartItems,
      setPage,
      setReservationOfferLoading,
      handleRemoveReservationOfferURLParam,
      handleCalculateCart,
      handleFetchAvailableRGs,
    ],
  );

  return {
    handleLoadReservationOfferOption: handleLoadReservationOfferOptionWithRatePlan,
    loadReservationOfferError: error,
  };
};
